import {
  getDuplicationMesh,
  getDeletionMesh,
  drawCalls,
} from "./callBlueprint";
import CnvCall from "./models/CnvCall";
import packLargeRegions from "../../helpers/packRegions/packLargeRegions";
import { CALL_CL } from "../../constants/options";
import ColorScale from "../../models/ColorScale";

function getDefaultState(track) {
  const { originAnalId, sampleCsv, previewCsv } = track.getSharedState();
  return {
    sampleDetails: {},
    duplicationCalls: [],
    deletionCalls: [],
    min: Infinity,
    max: -Infinity,
    chrSegments: null,
    originAnalId,
    sampleCsv: previewCsv || sampleCsv,
    floors: [],
    visRange: [],
    maxFloor: 1,
    floorHeight: 12,
  };
}

function readTrackController(track) {
  const deletionMesh = getDeletionMesh();
  const duplicationMesh = getDuplicationMesh();

  let state = getDefaultState(track);

  function onReset() {
    state = getDefaultState(track);
  }

  function onRangeChange({ chrSegments, start, end }) {
    state.visRange = [start, end];
    const { originAnalId, sampleCsv } = state;
    const areas = chrSegments.map((x) => ({
      ...x,
      x0: x.chr.region.x0,
      x1: x.chr.region.x1,
      g0: x.chr.region.g0,
      g1: x.chr.region.g1,
    }));

    track.loadPosition(areas, { originAnalId, sampleCsv });
  }

  function onDataReceived({ res, chr }) {
    const keys = Object.keys(res || {});
    const { floors } = state;
    const callKey = keys.find((x) => x.indexOf("sample.") === 0);
    const callDataArr = res[callKey] || [];
    const callKeys = (res.sample_keys || []).map((x) => x.replace(".", "_"));
    const keyIndex = callKeys.reduce((a, it, i) => ({ ...a, [it]: i }), {});
    const cnvCalls = callDataArr.map((x) => new CnvCall(x, chr, keyIndex));
    const deletions = cnvCalls.filter((x) => x.type === "deletion");
    const duplications = cnvCalls.filter((x) => x.type === "duplication");

    const all = [...deletions, ...duplications].sort(
      (a, b) => b.region.size - a.region.size
    );

    state.deletionCalls = [...state.deletionCalls, ...deletions];
    state.duplicationCalls = [...state.duplicationCalls, ...duplications];

    state.floors = packLargeRegions(all, floors, 1000);

    deletionMesh.setItems(state.deletionCalls);
    duplicationMesh.setItems(state.duplicationCalls);

    drawCalls(deletions, deletionMesh);
    drawCalls(duplications, duplicationMesh);

    recalcHeight();
    update();
  }

  function onRangeStabilized() {
    recalcHeight();
    update();
  }

  function onSharedStateChange() {
    const { previewCsv } = track.getSharedState();
    if (previewCsv !== state.sampleCsv) {
      track.reset();
    }
  }

  function recalcHeight() {
    const [s, e] = state.visRange;
    const { duplicationCalls: dups, deletionCalls: dels } = state;
    const levels = [...dups, ...dels]
      .filter((x) => x.region.hasOverlap(s, e))
      .map((x) => x.level);
    const max = Math.max(...levels) + 1;
    state.maxFloor = Math.max(1, max);
  }

  function update() {
    deletionMesh.uniforms.setMaxY(state.maxFloor);
    duplicationMesh.uniforms.setMaxY(state.maxFloor);

    track.requestRender();
    track.setHeight(state.maxFloor * state.floorHeight);
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const scaleName = config[CALL_CL];

    const vecs = new ColorScale(scaleName, 5)
      .setTheme(isDark)
      .setOpacity(0.6)
      .vectors.reverse();

    deletionMesh.uniforms.setColorMap(vecs);
    duplicationMesh.uniforms.setColorMap(vecs);
    update();
  }

  return {
    onReset,
    onSharedStateChange,
    onPreferencesChanged,
    onRangeChange,
    onRangeStabilized,
    onDataReceived,
    meshes: [deletionMesh, duplicationMesh],
  };
}

export default readTrackController;
